import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchIntegrationStatus,
  startIntegrationConnect,
  disconnectIntegration,
  saveIntegrationCredentials,
  clearIntegrationCredentials,
  importProcoreProjects,
  fetchProcoreProjects,
  fetchProcoreCompanies,
  createProjectsFromProcore,
  fetchProjects,
} from '../services/api';
import { Button, Card, Field, Select, Loading, ErrorText, errorMessage } from '../components/ui';
import { useMe } from '../hooks/useMe';

const OUTCOME_KEY = 'integrationOutcome';

// What each verdict from the OAuth callback means, in words the reader can act
// on. Every one of these but "connected" is something they have to go and do,
// so none of them may be left as a bare status code.
function outcomeExplanation({ status, provider }) {
  switch (status) {
    case 'wrong_account':
      // The one nobody predicts. The browser was already signed into the
      // provider - as somebody else - so no sign-in page appeared and the
      // whole trip looked instantaneous. It reads as the app refusing them
      // for no reason, which is why this says what actually happened.
      return `You are signed into ${provider} in this browser as a different person to the one signed into this app. ` +
        `${provider} did not show a sign-in page because it already had a session, so it looks as though nothing ` +
        `happened. Sign out of ${provider} in this browser, or open a private window, then connect again using the ` +
        `same email you use for this app.`;
    case 'identity_unavailable':
      return `${provider} would not say which account had authorised, so nothing was saved. Try again. If it keeps ` +
        `happening, that ${provider} account may not be allowed to read its own profile.`;
    case 'invalid_state':
      return `The connection took too long, or was started in a different browser. Start it again from this page.`;
    case 'error':
      return `${provider} declined the request. The usual reason is that this ${provider} account has no access to ` +
        `the company the app is installed on.`;
    default:
      return `Something went wrong partway through connecting to ${provider}. Try again, and quote the reference ` +
        `below if it keeps happening.`;
  }
}

// Third-party integrations (Procore and friends). The OAuth dance is driven by
// the backend: it hands back an authorize URL, and the provider redirects back
// to the API's callback, not to this app.
//
// Each tenant supplies their OWN app credentials, the same way AI connections
// work: the client id and secret are entered here and stored encrypted per
// tenant, so a customer connects their own Procore app rather than sharing
// ours. Without them the start endpoint refuses with "not configured", which
// used to be a dead end because this page had nowhere to enter them.
function CredentialsForm({ provider, onSaved, onError }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await saveIntegrationCredentials(provider.id, { clientId: clientId.trim(), clientSecret: clientSecret.trim() });
      setClientId('');
      setClientSecret('');
      await onSaved();
    } catch (err) { onError(errorMessage(err)); } finally { setBusy(false); }
  }

  return (
    <>
      <Field label="Client ID" hint={`From your ${provider.label} developer account.`}>
        <input className="input" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck={false} />
      </Field>
      <Field label="Client secret">
        <input className="input" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="new-password" />
      </Field>
      <Button compact onClick={save} disabled={!clientId.trim() || !clientSecret.trim() || busy}>
        {busy ? 'Saving…' : 'Save credentials'}
      </Button>
    </>
  );
}

// Procore is the only provider with resources to import so far, so the panel
// is keyed off the provider id. When a second provider gains the same shape,
// this becomes a capability declared in providers.js rather than a name check
// here - see the note in that file about adding providers being config-only.
function ProcorePanel({ isAdmin, onError }) {
  const [projects, setProjects] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  // Which fetched Procore projects to turn into app projects, by externalId.
  const [picked, setPicked] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  // Procore ids already present as app projects, so a re-import never offers
  // to create a second copy of something that is already here.
  const [existingIds, setExistingIds] = useState(() => new Set());
  // Projects already added are hidden by default.
  //
  // They were listed with the rest, badged and ticked, which on a Procore
  // company of any size buries the handful you came to add among dozens you
  // added months ago. Hidden rather than removed: "is this project already
  // in?" is a fair question, and the count below answers it in one click
  // instead of sending someone to the Projects page to check.
  const [showAdded, setShowAdded] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchProcoreProjects();
      setProjects(Array.isArray(data.projects) ? data.projects : []);
      const saved = data.integration?.companyId;
      if (saved) setCompanyId(String(saved));
    } catch { setProjects([]); }
    try {
      const appProjects = await fetchProjects();
      setExistingIds(new Set(
        appProjects.map((p) => String(p.procoreProjectId || '')).filter(Boolean),
      ));
    } catch { /* the list still renders; it just cannot mark what is already in */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addedCount = (projects || []).filter((p) => existingIds.has(String(p.externalId))).length;
  const visibleProjects = showAdded
    ? (projects || [])
    : (projects || []).filter((p) => !existingIds.has(String(p.externalId)));

  function toggle(externalId) {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(externalId)) next.delete(externalId); else next.add(externalId);
      return next;
    });
  }

  async function addPicked() {
    const chosen = (projects || []).filter((p) => picked.has(String(p.externalId)));
    if (!chosen.length) return;
    setAdding(true);
    setResult('');
    onError('');
    try {
      const created = await createProjectsFromProcore(chosen);
      setPicked(new Set());
      setResult(`Added ${created.length} project${created.length === 1 ? '' : 's'}. They're on the Projects page now.`);
      await load();
    } catch (err) { onError(errorMessage(err)); } finally { setAdding(false); }
  }

  // A token can see companies the app was never installed on, and Procore only
  // says so when a real request is refused - so let the admin pick rather than
  // defaulting to whichever happens to be first.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let live = true;
    fetchProcoreCompanies()
      .then((data) => { if (live) setCompanies(Array.isArray(data.companies) ? data.companies : []); })
      .catch(() => { if (live) setCompanies([]); });
    return () => { live = false; };
  }, [isAdmin]);

  async function importNow() {
    setBusy(true);
    setResult('');
    onError('');
    try {
      const data = await importProcoreProjects(companyId);
      // Says what it did. This fetches the Procore project LIST - it does not
      // create anything in the app, which is the next step down.
      setResult(`Found ${data.imported} project${data.imported === 1 ? '' : 's'} in ${data.companyName || 'Procore'}.`);
      await load();
    } catch (err) { onError(errorMessage(err)); } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <p className="muted" style={{ marginBottom: 6 }}>
        {projects === null
          ? 'Checking Procore projects…'
          : projects.length
            ? `${projects.length} Procore project${projects.length === 1 ? '' : 's'} available.`
            : 'No Procore projects fetched yet.'}
      </p>
      {result ? <p className="saved">{result}</p> : null}
      {/* Each option carries its id as well as its name: a Procore dev account
          gets a sandbox company mirroring the real one, same name and
          different id, so the name alone cannot tell them apart. */}
      {isAdmin && companies.length > 1 ? (
        <Select
          label="Procore company"
          hint="Pick the company your Procore app is installed on. Sandbox and production companies often share a name — the id tells them apart."
          value={companyId}
          options={companies.map((c) => ({ id: String(c.id), label: c.name, sublabel: `id ${c.id}` }))}
          onChange={setCompanyId}
        />
      ) : null}
      {isAdmin ? (
        <Button compact secondary onClick={importNow} disabled={busy}>
          {busy ? 'Fetching…' : projects?.length ? 'Refresh list from Procore' : 'Fetch projects from Procore'}
        </Button>
      ) : null}

      {/* Fetching from Procore and creating app projects are deliberately two
          steps. The fetched list is also what the "Send to Procore" picker on
          a document uses, so it is useful on its own - and creating every
          project a large Procore company has would bury the Projects page and
          run straight into the plan's project limit. */}
      {isAdmin && projects?.length ? (
        <div className="procore-picker">
          <p className="card-hint" style={{ marginTop: 12 }}>
            Tick the ones to add as projects in this app. Documents can be pushed back to any
            Procore project whether or not it is added here.
          </p>
          {addedCount ? (
            <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span className="card-hint" style={{ margin: 0 }}>
                {addedCount} already added{showAdded ? '' : ' and hidden'}.
              </span>
              <Button compact secondary onClick={() => setShowAdded((v) => !v)}>
                {showAdded ? 'Hide them' : 'Show them'}
              </Button>
            </div>
          ) : null}
          {!visibleProjects.length && !showAdded ? (
            <p className="card-hint">Every project in this Procore company has already been added.</p>
          ) : null}
          {visibleProjects.map((p) => {
            const id = String(p.externalId);
            const already = existingIds.has(id);
            return (
              <label key={id} className={already ? 'procore-option is-added' : 'procore-option'}>
                <input
                  type="checkbox"
                  checked={already || picked.has(id)}
                  disabled={already}
                  onChange={() => toggle(id)}
                />
                <span className="procore-option-name">{p.name}</span>
                {p.code ? <span className="procore-option-code">{p.code}</span> : null}
                {already ? <span className="rate-badge">added</span> : null}
              </label>
            );
          })}
          <Button compact onClick={addPicked} disabled={adding || !picked.size}>
            {adding ? 'Adding…' : `Add ${picked.size || ''} to app`.replace('  ', ' ')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default function ConnectionsPage() {
  // Saving credentials and connecting are both admin-only server-side.
  const { isAdmin } = useMe();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Which provider is showing its credential form, so an already-configured
  // one can be replaced without the form cluttering every card.
  const [editing, setEditing] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setStatus(await fetchIntegrationStatus()); } catch (err) { setError(errorMessage(err)); }
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  // The OAuth callback's verdict, kept apart from `error` and survived across
  // a reload.
  //
  // It used to go into `error`, which load() clears on every refresh and which
  // a remount loses outright - so a refused connection showed as a red flash
  // too quick to read, and the person was left knowing only that something had
  // gone wrong. That was tolerable when a refusal meant a bug. It is not now:
  // being turned away is a NORMAL outcome of connecting, and the words are the
  // only thing that tells somebody what to do about it.
  //
  // sessionStorage because the trip home from Procore is a full page load, and
  // whatever the app does on the way - a re-auth redirect, a router remount -
  // must not be able to swallow the one message that explains the journey.
  const [outcome, setOutcome] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(OUTCOME_KEY) || 'null'); } catch { return null; }
  });

  const dismissOutcome = useCallback(() => {
    setOutcome(null);
    try { sessionStorage.removeItem(OUTCOME_KEY); } catch { /* private window */ }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (!status) return;
    const next = { status, provider: params.get('integration') || 'The provider' };
    setOutcome(next);
    try { sessionStorage.setItem(OUTCOME_KEY, JSON.stringify(next)); } catch { /* private window */ }
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // Fetch the provider's authorize URL first, then navigate to the PROVIDER -
  // never to our own API. Navigating to the API instead means the token has to
  // ride in the query string, and the act of fetching it can trigger MSAL's
  // re-auth redirect, which lands back on the app origin and silently drops
  // the whole flow.
  async function connect(providerId) {
    setError('');
    try {
      window.location.href = await startIntegrationConnect(providerId);
    } catch (err) { setError(errorMessage(err)); }
  }

  async function afterCredentialChange() {
    setEditing('');
    setNotice('Credentials saved.');
    await load();
  }

  const providers = status?.providers || [];

  return (
    <>
      <h1>Integrations</h1>
      <p className="sub">
        Connect external systems using your own app credentials. Secrets are encrypted by the backend and never
        returned to this browser.
      </p>
      <ErrorText>{error}</ErrorText>
      {notice && <p className="saved">{notice}</p>}

      {/* The verdict on the last connect attempt. Stays put until dismissed,
          because every outcome but "connected" asks the reader to go and do
          something, and the raw status code is what to quote if they ask for
          help. */}
      {outcome && (
        outcome.status === 'connected'
          ? <p className="saved">{outcome.provider} connected. <button type="button" className="linklike" onClick={dismissOutcome}>Dismiss</button></p>
          : (
            <div className="card" style={{ marginBottom: 12 }}>
              <p style={{ marginTop: 0 }}><strong>{outcome.provider} was not connected.</strong></p>
              <p className="muted" style={{ marginTop: 0 }}>{outcomeExplanation(outcome)}</p>
              <p className="muted" style={{ marginTop: 0, fontSize: '0.85em' }}>Reference: <code>{outcome.status}</code></p>
              <Button compact secondary onClick={dismissOutcome}>Dismiss</Button>
            </div>
          )
      )}

      {loading ? <Loading /> : providers.map((p) => (
        <Card key={p.id} title={p.label || p.id} badge={p.connected ? 'connected' : undefined} hint={p.description}>
          {p.hasCredentials ? (
            <p className="muted" style={{ marginTop: 0 }}>
              {p.hasCustomCredentials
                ? `Using your own app credentials${p.credentialClientId ? ` (client ID ${p.credentialClientId})` : ''}.`
                : 'Using the credentials configured on this deployment.'}
            </p>
          ) : (
            <p className="muted" style={{ marginTop: 0 }}>
              Not configured yet — enter your {p.label} client ID and secret to connect.
            </p>
          )}

          {/* No credentials means Connect can only fail, so ask for them first
              rather than offering a button that returns "not configured". */}
          {isAdmin && (!p.hasCredentials || editing === p.id) && (
            <CredentialsForm provider={p} onSaved={afterCredentialChange} onError={setError} />
          )}

          {/* Which provider account this person's own connection carries. The
              server refuses to save one belonging to anybody else, so this is
              a confirmation rather than a warning - useful mainly to somebody
              who holds more than one Procore account. */}
          {p.connected && p.connectedAs && (
            <p className="muted" style={{ marginTop: 0 }}>
              Connected as <strong>{p.connectedAs}</strong>
              {p.connectedAsName ? ` (${p.connectedAsName})` : ''}. What you import and send uses your own
              {' '}{p.label} permissions, and is attributed to you.
            </p>
          )}

          {/* A connection is per person, so "not connected" is about the
              reader rather than about the workspace. Without saying so, an
              admin who has connected and a colleague who has not see the same
              card and reasonably conclude the integration is broken. */}
          {!p.connected && p.hasCredentials && (
            <p className="muted" style={{ marginTop: 0 }}>
              You have not connected your own {p.label} account yet. Connecting uses your {p.label} sign-in, so you
              get exactly the access you already have there — nobody else's.
            </p>
          )}

          {p.connected && p.id === 'procore' ? <ProcorePanel isAdmin={isAdmin} onError={setError} /> : null}

          <div className="row" style={{ marginTop: 10 }}>
            {/* Neither is admin-gated any more, and the reason is the same for
                both: a connection is now the connector's own.

                Connect was gated while one connection served everybody -
                letting any member create it meant letting any member choose
                whose Procore access the whole workspace would borrow. It now
                grants nobody anything but the connector, and gating it would
                leave Procore usable only by admins. Disconnect was gated for
                the matching reason, that one click took Procore away from the
                whole organisation; now it only ever removes your own.

                What replaces the gate is Procore: somebody with no Procore
                account cannot finish the dance, so they end up with no
                connection, without this app holding an opinion about them. */}
            {p.connected
              ? <Button compact danger onClick={async () => { await disconnectIntegration(p.id); load(); }}>Disconnect</Button>
              : <Button compact disabled={!p.hasCredentials} onClick={() => connect(p.id)}>Connect</Button>}

            {isAdmin && p.hasCredentials && editing !== p.id && (
              <Button compact secondary onClick={() => setEditing(p.id)}>Replace credentials</Button>
            )}
            {isAdmin && editing === p.id && (
              <Button compact secondary onClick={() => setEditing('')}>Cancel</Button>
            )}
            {isAdmin && p.hasCustomCredentials && editing !== p.id && (
              <Button compact secondary onClick={async () => {
                await clearIntegrationCredentials(p.id);
                await afterCredentialChange();
              }}>Clear</Button>
            )}
          </div>
        </Card>
      ))}
      {!loading && !providers.length && <p className="muted">No integrations are configured for this organisation.</p>}
      {!loading && !isAdmin && providers.length > 0 && (
        <p className="muted">Only an owner or admin can connect or change integrations.</p>
      )}
    </>
  );
}
